import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cookies from "js-cookie";
import {
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiRefreshCw,
  FiX,
} from "react-icons/fi";
import { MdHistory } from "react-icons/md";

import { API_PROJECT_MEMBER_HISTORY } from "../../config/Endpoints";
import { getApi } from "../../utils/api";
import "./ProjectMemberHistoryDrawer.css";

const STATUS_META = {
  0: { label: "Inactive", action: "Membership updated", tone: "inactive" },
  1: { label: "Allocated", action: "Member allocated", tone: "allocated" },
  2: {
    label: "Deallocated",
    action: "Member deallocated",
    tone: "deallocated",
  },
};

const getEventDate = (item) =>
  item.updatedAt || item.endDate || item.createdAt || item.startDate || null;

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatDateOnly = (value) => {
  const date = parseDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatLabel = (value, fallback = "Not specified") => {
  if (!value) return fallback;
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const getDayKey = (value) => {
  const date = parseDate(value);
  if (!date) return "unknown";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const getDayHeading = (value) => {
  const date = parseDate(value);
  if (!date) return "Date unavailable";

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const eventStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const difference = Math.round(
    (todayStart.getTime() - eventStart.getTime()) / 86400000,
  );

  if (difference === 0) return "Today";
  if (difference === 1) return "Yesterday";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: today.getFullYear() === date.getFullYear() ? undefined : "numeric",
  }).format(date);
};

const getInitials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

function ProjectMemberHistoryDrawer({ projectId, projectName, employees = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState("ALL");
  const [expandedId, setExpandedId] = useState(null);
  const closeButtonRef = useRef(null);
  const accessToken = Cookies.get("accessToken");

  const employeeNameById = useMemo(
    () =>
      new Map(
        employees.map((employee) => [
          String(employee.value ?? employee.id),
          employee.label ?? employee.name,
        ]),
      ),
    [employees],
  );

  const getEmployeeName = (item) =>
    String(item?.fullName ?? "").trim() ||
    employeeNameById.get(String(item?.memberId)) ||
    `Employee #${item?.memberId}`;

  const loadHistory = async () => {
    if (projectId == null) return;
    setLoading(true);
    setError("");
    try {
      const response = await getApi(
        `${API_PROJECT_MEMBER_HISTORY}/${projectId}`,
        accessToken,
      );
      const payload = response.data?.data ?? response.data;
      const rows = Array.isArray(payload) ? payload : [];
      const sorted = [...rows].sort((left, right) => {
        const leftTime = parseDate(getEventDate(left))?.getTime() ?? 0;
        const rightTime = parseDate(getEventDate(right))?.getTime() ?? 0;
        return rightTime - leftTime;
      });
      setHistory(sorted);
      setExpandedId(sorted[0]?.id ?? null);
    } catch (requestError) {
      setHistory([]);
      setError(
        requestError?.response?.data?.message ||
          "Unable to load project member history.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    loadHistory();
    closeButtonRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
    // Reload whenever the drawer opens for the selected project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  useEffect(() => {
    setSelectedMemberId("ALL");
  }, [projectId]);

  const memberOptions = useMemo(() => {
    const ids = [...new Set(history.map((item) => String(item.memberId)))];
    return ids
      .map((id) => {
        const historyItem = history.find((item) => String(item.memberId) === id);
        return {
          id,
          name:
            String(historyItem?.fullName ?? "").trim() ||
            employeeNameById.get(id) ||
            `Employee #${id}`,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [history, employeeNameById]);

  const groupedHistory = useMemo(() => {
    const filtered =
      selectedMemberId === "ALL"
        ? history
        : history.filter(
            (item) => String(item.memberId) === selectedMemberId,
          );

    const groups = [];
    filtered.forEach((item) => {
      const eventDate = getEventDate(item);
      const key = getDayKey(eventDate);
      let group = groups.find((entry) => entry.key === key);
      if (!group) {
        group = { key, heading: getDayHeading(eventDate), items: [] };
        groups.push(group);
      }
      group.items.push(item);
    });
    return groups;
  }, [history, selectedMemberId]);

  const drawer = isOpen ? (
    <div className="pmh-layer" role="presentation">
      <button
        type="button"
        className="pmh-backdrop"
        aria-label="Close member history"
        onClick={() => setIsOpen(false)}
      />
      <aside
        className="pmh-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pmh-title"
      >
        <header className="pmh-header">
          <div>
            <div className="pmh-title-row">
              <span className="pmh-title-icon" aria-hidden="true">
                <MdHistory />
              </span>
              <h2 id="pmh-title">Member history</h2>
            </div>
            <p>{projectName || "Project"}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="pmh-icon-button"
            aria-label="Close member history"
            onClick={() => setIsOpen(false)}
          >
            <FiX />
          </button>
        </header>

        <div className="pmh-toolbar">
          <label htmlFor={`pmh-member-filter-${projectId}`}>Member</label>
          <select
            id={`pmh-member-filter-${projectId}`}
            value={selectedMemberId}
            onChange={(event) => setSelectedMemberId(event.target.value)}
          >
            <option value="ALL">All members</option>
            {memberOptions.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="pmh-icon-button pmh-refresh"
            aria-label="Refresh history"
            title="Refresh history"
            onClick={loadHistory}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "pmh-spinning" : ""} />
          </button>
        </div>

        <div className="pmh-content">
          {loading && history.length === 0 ? (
            <div className="pmh-state" role="status">
              <span className="pmh-loader" />
              <strong>Loading history</strong>
              <span>Fetching project membership changes...</span>
            </div>
          ) : error ? (
            <div className="pmh-state pmh-error" role="alert">
              <strong>History could not be loaded</strong>
              <span>{error}</span>
              <button type="button" onClick={loadHistory}>
                Try again
              </button>
            </div>
          ) : groupedHistory.length === 0 ? (
            <div className="pmh-state">
              <FiClock aria-hidden="true" />
              <strong>No history available</strong>
              <span>
                {selectedMemberId === "ALL"
                  ? "Membership changes for this project will appear here."
                  : "No changes were found for this member."}
              </span>
            </div>
          ) : (
            groupedHistory.map((group) => (
              <section className="pmh-day" key={group.key}>
                <h3>{group.heading}</h3>
                <div className="pmh-timeline">
                  {group.items.map((item) => {
                    const status = STATUS_META[item.status] || STATUS_META[0];
                    const isExpanded = expandedId === item.id;
                    const employeeName = getEmployeeName(item);
                    return (
                      <article
                        className={`pmh-event pmh-event--${status.tone}`}
                        key={item.id}
                      >
                        <span className="pmh-timeline-dot" aria-hidden="true" />
                        <button
                          type="button"
                          className="pmh-event-summary"
                          aria-expanded={isExpanded}
                          onClick={() =>
                            setExpandedId(isExpanded ? null : item.id)
                          }
                        >
                          {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                          <span className="pmh-avatar">
                            {getInitials(employeeName)}
                          </span>
                          <span className="pmh-event-copy">
                            <strong>{employeeName}</strong>
                            <small>
                              {status.action} · {formatDateTime(getEventDate(item))}
                            </small>
                          </span>
                          <span className={`pmh-status pmh-status--${status.tone}`}>
                            {status.label}
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="pmh-event-details">
                            <dl>
                              <div>
                                <dt>Member type</dt>
                                <dd>{formatLabel(item.memberType, "Main member")}</dd>
                              </div>
                              <div>
                                <dt>Designation</dt>
                                <dd>{formatLabel(item.projectDesignation)}</dd>
                              </div>
                              <div>
                                <dt>Start date</dt>
                                <dd>{formatDateOnly(item.startDate)}</dd>
                              </div>
                              <div>
                                <dt>End date</dt>
                                <dd>{formatDateOnly(item.endDate)}</dd>
                              </div>
                            </dl>
                            {item.expertise && (
                              <div className="pmh-detail-block">
                                <span>Project expertise</span>
                                <p>{item.expertise}</p>
                              </div>
                            )}
                            {item.reason && (
                              <div className="pmh-detail-block pmh-reason">
                                <span>Reason</span>
                                <p>{item.reason}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </aside>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        className="btn pmh-trigger"
        onClick={() => setIsOpen(true)}
      >
        <MdHistory aria-hidden="true" />
        History
      </button>
      {typeof document !== "undefined" && drawer
        ? createPortal(drawer, document.body)
        : null}
    </>
  );
}

export default ProjectMemberHistoryDrawer;
